import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { GA_NBA_CATEGORY, GA_NBA_EVENTS } from 'modules/ga/generalEvents';
import { NBA_CHAT_ROOM } from 'chat/constants';
import { useTranslation } from 'utils/modifiedTranslation';
import { useChat } from 'chat/useChat';
import Button from 'new-ui/Components/Button';
import LinkButton from 'new-ui/Components/LinkButton';
import { ReactComponent as ArrowLeft, ReactComponent as ArrowRight } from 'assets/icons/arrow_left_2.svg';
import { THEMES } from 'new-ui/constants';
import app from 'new-ui/app';
import { ACTION_TRANSITION_DIRECTION } from '../nba.constants';
import styles from './ActionCard.module.css';

const ActionCard = ({
  title = 'What to do next?',
  buttonText = 'Discuss with your oncologist',
  buttonAction,
  description = 'Discuss your treatment options in detail with your oncology.',
  linkText = 'Learn how to',
  linkAction,
  actionName,
  onPrev,
  onNext,
  showPrev = true,
  showNext = true,
  totalSteps,
  currentStep = 1,
  dynamicContentComponent = null,
  transitionDirection = ACTION_TRANSITION_DIRECTION.RIGHT,
}) => {
  const { t } = useTranslation();
  const { openChatPopup } = useChat(NBA_CHAT_ROOM);

  const slideClass = transitionDirection === ACTION_TRANSITION_DIRECTION.LEFT
    ? styles.actionSlideLeft
    : styles.actionSlideRight;

  const contentWrapperClassName = cx(styles.contentWrapper, slideClass);

  const handleDefaultLearnMoreClick = () => {
    if (!actionName) {
      console.warn('ActionCard: actionName prop is missing for default learn more.');
      return;
    }

    app.sendGoogleAnalyticsEvent(
      GA_NBA_CATEGORY,
      GA_NBA_EVENTS.CTA_CLICKED,
      { cta_name: actionName },
    );

    openChatPopup({
      data: { initialUserMessage: `Please give me more information about: "${actionName}"` },
    });
  };

  return (
    <div className={styles.actionCard}>
      <button
        type="button"
        className={`${styles.arrow} ${styles.arrowLeft}`}
        onClick={onPrev}
        aria-label="Previous action"
        disabled={!showPrev}
      >
        <ArrowLeft />
      </button>

      <div className={contentWrapperClassName}>
        {dynamicContentComponent && dynamicContentComponent.Component ? (
          <dynamicContentComponent.Component {...dynamicContentComponent.props} />
        ) : (
          <>
            <h3 className={styles.title}>{t(title)}</h3>
            <div className={styles.content}>
              <Button
                title={t(buttonText)}
                action={buttonAction}
                theme={THEMES.ORANGE_FULL}
                className={styles.actionButton}
              />
              <p className={styles.description}>{t(description)}</p>
              <LinkButton
                title={t(linkText)}
                action={linkAction || handleDefaultLearnMoreClick}
                className={styles.learnMoreLink}
              />
            </div>
          </>
        )}
        {totalSteps > 1 && (
          <div
            className={styles.stepDotsContainer}
            role="status"
            aria-label={`Action ${currentStep} of ${totalSteps}`}
          >
            {Array.from({ length: totalSteps }).map((_, index) => (
              <span
                key={index}
                className={cx(styles.stepDot, { [styles.stepDotActive]: index + 1 === currentStep })}
              />
            ))}
          </div>
        )}
      </div>

      <button
        type="button"
        className={`${styles.arrow} ${styles.arrowRight}`}
        onClick={onNext}
        aria-label="Next action"
        disabled={!showNext}
      >
        <ArrowRight style={{ transform: 'rotate(180deg)' }} />
      </button>
    </div>
  );
};

ActionCard.propTypes = {
  title: PropTypes.string,
  buttonText: PropTypes.string,
  buttonAction: PropTypes.func.isRequired,
  description: PropTypes.string,
  linkText: PropTypes.string,
  linkAction: PropTypes.func,
  actionName: PropTypes.string,
  onPrev: PropTypes.func,
  onNext: PropTypes.func,
  showPrev: PropTypes.bool,
  showNext: PropTypes.bool,
  totalSteps: PropTypes.number.isRequired,
  currentStep: PropTypes.number,
  dynamicContentComponent: PropTypes.shape({
    Component: PropTypes.elementType,
    props: PropTypes.object,
  }),
  transitionDirection: PropTypes.oneOf(Object.values(ACTION_TRANSITION_DIRECTION)),
};

export default ActionCard;
